import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { Sparkles, Loader2, Eye, Send, Trash2, RefreshCw, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SEED_TOPICS = [
  { label: "O meu filho é gay e agora?", topic: "O meu filho é gay e agora? Um guia sereno para pais e cuidadores", audience: "pais recém-informados da orientação do filho" },
  { label: "A minha filha adolescente não me conta nada", topic: "A minha filha adolescente não me conta nada — como reconstruir a comunicação sem invadir", audience: "pais de adolescentes 12-18 anos" },
  { label: "Filhos com distúrbios alimentares", topic: "Quando o meu filho começa a esconder as refeições — sinais precoces de distúrbios alimentares e o que fazer", audience: "pais preocupados com anorexia, bulimia ou compulsão alimentar" },
  { label: "Bullying na escola", topic: "O meu filho é vítima de bullying — protocolo prático em 30 dias", audience: "pais de crianças 8-15 vítimas de bullying" },
  { label: "Ansiedade e ataques de pânico", topic: "Ansiedade em adolescentes e crises de pânico — guia para pais", audience: "pais de adolescentes com ansiedade" },
  { label: "Divórcio e coparentalidade", topic: "Coparentalidade saudável depois do divórcio — o que os miúdos precisam de ouvir", audience: "pais separados/divorciados" },
];

export default function EbooksPage() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyGenerate, setBusyGenerate] = useState(false);
  const [customTopic, setCustomTopic] = useState("");

  const load = async () => {
    const { data } = await api.get("/ebooks/admin");
    setItems(data.ebooks || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const generate = async (topic, audience) => {
    setBusyGenerate(true);
    // Fire the LLM POST — expect ~90-150s. Poll list every 8s until item appears.
    const seenBefore = items.length;
    api.post("/ebooks/generate", {
      topic, audience: audience || "pais e cuidadores",
      price_chf: 4.90, lang: "pt",
    }).then((r) => {
      toast.success("Ebook gerado — revê e publica.");
      load();
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || "Falha na geração — vê o painel.");
    });
    // Also start a polling refresh so operator sees the new row even if the
    // Cloudflare edge kills our POST connection.
    const started = Date.now();
    const poll = setInterval(async () => {
      try {
        const { data } = await api.get("/ebooks/admin");
        if ((data.ebooks || []).length > seenBefore) {
          setItems(data.ebooks || []);
          setBusyGenerate(false);
          clearInterval(poll);
        }
      } catch {/* ignore */}
      if (Date.now() - started > 240000) {
        clearInterval(poll);
        setBusyGenerate(false);
      }
    }, 8000);
  };

  const togglePublish = async (id) => {
    try {
      const { data } = await api.post(`/ebooks/${id}/publish`);
      toast.success(data.published ? "Publicado" : "Despublicado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };
  const rebuild = async (id) => {
    try {
      const { data } = await api.post(`/ebooks/${id}/rebuild-pdf`);
      toast.success(`PDF regenerado (${Math.round(data.bytes/1024)} KB)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Apagar este ebook? (soft delete)")) return;
    await api.delete(`/ebooks/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="ebooks-admin-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-light text-3xl tracking-tight">
            {t(lang, "ebooks_admin_title")}
          </h1>
          <p className="mt-1 text-white/50 text-sm max-w-2xl">
            {t(lang, "ebooks_admin_subtitle")}
          </p>
        </div>
      </div>

      {/* Seed topics — one-click generation for the sensitive-topic playbook */}
      <div className="hq-card p-5">
        <div className="font-display text-lg mb-2 flex items-center gap-2">
          <Sparkles size={16} className="text-[color:var(--hq-cyan)]" />
          {t(lang, "ebooks_seed_title")}
        </div>
        <p className="text-white/50 text-xs mb-4">{t(lang, "ebooks_seed_body")}</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {SEED_TOPICS.map((s) => (
            <button
              key={s.label}
              data-testid={`ebooks-seed-${s.label.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}
              onClick={() => generate(s.topic, s.audience)}
              disabled={busyGenerate}
              className="text-left px-3 py-2 rounded-md border border-white/10 hover:border-[color:var(--hq-cyan)]/40 text-xs disabled:opacity-50"
            >
              <div className="text-white/90 font-medium">{s.label}</div>
              <div className="text-white/40 text-[10px] mt-0.5 truncate">{s.audience}</div>
            </button>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="text-white/40 font-mono text-[10px] uppercase mb-2">Ou escreve o teu próprio tema</div>
          <div className="flex gap-2">
            <input
              data-testid="ebooks-custom-topic"
              type="text"
              value={customTopic}
              onChange={(e) => setCustomTopic(e.target.value)}
              placeholder="ex: Como conversar sobre puberdade com uma filha de 10 anos"
              className="hq-input flex-1 text-sm"
            />
            <button
              data-testid="ebooks-custom-generate-btn"
              onClick={() => customTopic.trim() && generate(customTopic.trim())}
              disabled={busyGenerate || !customTopic.trim()}
              className="hq-btn hq-btn-primary disabled:opacity-50"
            >
              {busyGenerate ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
              {t(lang, "ebooks_generate")}
            </button>
          </div>
          {busyGenerate && (
            <div className="mt-2 font-mono text-[10px] uppercase text-[color:var(--hq-cyan)]">
              A escrever · ~2 minutos · podes fechar e voltar
            </div>
          )}
        </div>
      </div>

      {/* Existing ebooks */}
      <div className="hq-card p-5">
        <div className="font-display text-lg mb-4">
          {t(lang, "ebooks_admin_library")} · {items.length}
        </div>
        {loading ? (
          <div className="text-center py-10 text-white/40 text-xs">…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-white/40 text-sm">
            {t(lang, "ebooks_admin_empty")}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((e) => (
              <div
                key={e.id}
                data-testid={`ebook-row-${e.slug}`}
                className="grid grid-cols-[auto_1fr_auto] gap-4 items-center p-3 rounded-md border border-white/10 hover:border-white/30"
              >
                <div
                  className="w-14 h-20 rounded flex items-center justify-center text-[9px] text-white/70 font-mono uppercase"
                  style={{ background: `linear-gradient(135deg, ${e.cover_hex || "#0E7C86"} 0%, #050505 130%)` }}
                >
                  Guide
                </div>
                <div className="min-w-0">
                  <div className="text-white text-sm font-medium truncate">{e.title}</div>
                  <div className="text-white/40 text-[10px] font-mono uppercase mt-0.5">
                    {e.author_persona || "—"} · {e.word_count || 0} palavras · CHF {(e.price_chf ?? 0).toFixed(2)}
                  </div>
                  <div className="mt-1 flex gap-2 flex-wrap">
                    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${
                      e.published
                        ? "border border-[color:var(--hq-success)]/40 text-[color:var(--hq-success)]"
                        : "border border-white/10 text-white/40"
                    }`}>
                      {e.published ? "publicado" : "rascunho"}
                    </span>
                    {e.pdf_error && (
                      <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border border-red-400/40 text-red-400">
                        pdf erro
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <a
                    data-testid={`ebook-preview-${e.slug}`}
                    href={`${BACKEND_URL}/api/ebooks/${e.id}/preview.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hq-btn hq-btn-ghost text-xs"
                    title="Preview PDF"
                  >
                    <Eye size={11} />
                  </a>
                  <button
                    data-testid={`ebook-rebuild-${e.slug}`}
                    onClick={() => rebuild(e.id)}
                    className="hq-btn hq-btn-ghost text-xs"
                    title="Regenerar PDF"
                  >
                    <RefreshCw size={11} />
                  </button>
                  <button
                    data-testid={`ebook-publish-${e.slug}`}
                    onClick={() => togglePublish(e.id)}
                    className={`hq-btn text-xs ${e.published ? "hq-btn-ghost" : "hq-btn-primary"}`}
                  >
                    <Send size={11} />
                    {e.published ? "Despublicar" : "Publicar"}
                  </button>
                  <button
                    data-testid={`ebook-delete-${e.slug}`}
                    onClick={() => del(e.id)}
                    className="hq-btn hq-btn-ghost text-xs text-red-400"
                    title="Apagar"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
